import { useState, useEffect } from "react";
import {
  Upload,
  Trash2,
  Star,
  ArrowLeft,
  Save,
  Home,
  FileText,
  ImagePlus,
  CheckSquare,
} from "lucide-react";
import { useCorporateArchitectureDesign } from "../useHook";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { sanitizePositionInput } from "../../../../Utils/positionInput";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../Components/Shared/ImageSeoFields";
import { RichTextEditor } from "../../../../Components/Shared/RichTextEditor";

/** Shared create/edit form — uses RichTextEditor (not ReactQuill). */
const emptyFormData = {
  title: "",
  title_ur: "",
  plot_dimension: "",
  covered_area: "",
  floors: 0,
  customUrl: "",
  description: "",
  description_ur: "",
  features_ur: "",
  servant_quarters: false,
  terrace: false,
  car_parking: false,
  images: [],
  imagePreviews: [],
  imagePositions: [],
  imageAlts: [],
  imageTitles: [],
  primaryImageIndex: 0,
};

const cardIcon =
  "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]";
const featureChip =
  "flex items-center gap-2 rounded-xl border border-[#15173d]/10 bg-[#faf8fa] p-3 cursor-pointer transition hover:border-[#982598]/35 hover:bg-[#982598]/5";

export const ArchitectureDesignForm = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const {
    addCorporateArchitectureDesign,
    updateCorporateArchitectureDesign,
    getCorporateArchitectureDesignById,
    updateCorporateArchitectureDesignPrimaryImage,
  } = useCorporateArchitectureDesign();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [formData, setFormData] = useState({ ...emptyFormData });

  useEffect(() => {
    if (isEditMode && editId) {
      loadDesignForEdit(editId);
    }
  }, [isEditMode, editId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleDescriptionUrChange = (value) => {
    setFormData((prev) => ({ ...prev, description_ur: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const checkImageResolution = (file) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width === 1200 && img.height === 900);
        img.src = URL.createObjectURL(file);
      });

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        const newValidImages = [];
        const newImagePreviews = [];
        const invalidNames = [];
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            invalidNames.push(files[index].name);
          }
        });
        if (invalidNames.length > 0) {
          Swal.fire({
            icon: "error",
            title: "Invalid Image Resolution",
            html:
              invalidNames.length === 1
                ? `Image <b>"${invalidNames[0]}"</b> does not have the required resolution of <b>1200×900</b>. Please upload the correct resolution.`
                : `These images do not have the required resolution of <b>1200×900</b>:<br/><br/>${invalidNames
                    .map((n) => `• ${n}`)
                    .join("<br/>")}`,
            confirmButtonText: "OK",
          });
        }
        if (!newValidImages.length) return;
        setImageError("");
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex =
            currentImageCount === 0 ? 0 : prev.primaryImageIndex;
          const combinedCount = currentImageCount + newValidImages.length;
          let next = 1;
          const imagePositions = Array.from({ length: combinedCount }, (_, i) =>
            i === newPrimaryIndex ? "" : String(next++),
          );
          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            imageAlts: [
              ...(prev.imageAlts || []),
              ...newValidImages.map(() => ""),
            ],
            imageTitles: [
              ...(prev.imageTitles || []),
              ...newValidImages.map(() => ""),
            ],
            imagePositions,
            primaryImageIndex: newPrimaryIndex,
          };
        });
      },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith("/")) {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if (
      (!formData.images || formData.images.length === 0) &&
      !(isEditMode && existingImages.length > 0)
    ) {
      setImageError("At least one image is required");
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    try {
      const apiData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (
          key !== "image" &&
          key !== "images" &&
          key !== "imagePreviews" &&
          key !== "imagePositions" &&
          key !== "imageAlts" &&
          key !== "imageTitles" &&
          key !== "primaryImageIndex"
        ) {
          apiData.append(key, formData[key]);
        }
      });

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append("image", image);
          apiData.append("isPrimary", index === formData.primaryImageIndex);
          apiData.append(
            "imagePosition",
            formData.imagePositions?.[index] != null
              ? formData.imagePositions[index]
              : index + 1,
          );
          apiData.append(
            "imageAlt",
            String(formData.imageAlts?.[index] ?? "").trim(),
          );
          apiData.append(
            "imageTitle",
            String(formData.imageTitles?.[index] ?? "").trim(),
          );
        });
      }

      if (isEditMode && existingImages.length > 0) {
        apiData.append(
          "existing_images",
          JSON.stringify(
            existingImages.map((image) => ({
              id: image.id,
              image_url: image.image_url,
              position: image.position,
              is_primary: !!image.is_primary,
              image_alt: image.image_alt || "",
              image_title: image.image_title || "",
            })),
          ),
        );
      }

      const response = isEditMode
        ? await updateCorporateArchitectureDesign(editId, apiData)
        : await addCorporateArchitectureDesign(apiData);

      if (response && response.success === false) {
        throw new Error(
          response.message || "Failed to save corporate architecture design.",
        );
      }

      setExistingImages([]);
      resetForm();
      Swal.fire({
        icon: "success",
        title: isEditMode
          ? "Corporate Architecture Design Updated"
          : "Corporate Architecture Design Added",
        text: response?.message,
        showConfirmButton: false,
        timer: 1500,
      });
      navigate("/corporate-architecture-design");
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          "Failed to save corporate architecture design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    (formData.imagePreviews || []).forEach((url) => URL.revokeObjectURL(url));
    setFormData({ ...emptyFormData });
  };

  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    const updatedPreviews = [...(formData.imagePreviews || [])];
    const updatedPositions = [...(formData.imagePositions || [])];
    const updatedAlts = [...(formData.imageAlts || [])];
    const updatedTitles = [...(formData.imageTitles || [])];
    if (updatedPreviews[index]) URL.revokeObjectURL(updatedPreviews[index]);
    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    updatedPositions.splice(index, 1);
    updatedAlts.splice(index, 1);
    updatedTitles.splice(index, 1);
    let newPrimaryIndex = formData.primaryImageIndex;
    if (index <= formData.primaryImageIndex) {
      newPrimaryIndex = Math.max(0, formData.primaryImageIndex - 1);
    }
    setFormData({
      ...formData,
      images: updatedImages,
      imagePreviews: updatedPreviews,
      imagePositions: updatedPositions,
      imageAlts: updatedAlts,
      imageTitles: updatedTitles,
      primaryImageIndex: newPrimaryIndex,
    });
    if (updatedImages.length === 0) {
      setImageError("At least one image is required");
    }
  };

  const onImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const images = [...(prev.images || [])];
      const positions = images.map((_, i) => prev.imagePositions?.[i] ?? i + 1);
      positions[index] = cleaned;
      return { ...prev, imagePositions: positions };
    });
  };

  const setPrimaryImage = (index) => {
    setFormData((prev) => {
      const images = prev.images || [];
      let next = 1;
      const imagePositions = images.map((_, i) =>
        i === index ? "" : String(next++),
      );
      return { ...prev, primaryImageIndex: index, imagePositions };
    });
  };

  const onExistingImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onExistingImageSeoChange = (index, field, value) => {
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlts" : "imageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const handleSetExistingPrimary = async (imageId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to change the primary image of this corporate architecture design?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
    });
    if (!result.isConfirmed) return;
    try {
      await updateCorporateArchitectureDesignPrimaryImage(imageId);
      setExistingImages((prev) => {
        let nextIndex = 1;
        return prev.map((img) => {
          if (img.id === imageId) {
            return { ...img, is_primary: true, position: "" };
          }
          return { ...img, is_primary: false, position: nextIndex++ };
        });
      });
      Swal.fire({
        title: "Success",
        text: "Primary image has been updated",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update primary image:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update primary image. Please try again.",
        icon: "error",
      });
    }
  };

  const loadDesignForEdit = async (id) => {
    try {
      setIsSubmitting(true);
      const design = await getCorporateArchitectureDesignById(id);
      if (!design) throw new Error("Corporate architecture design not found");
      setFormData({
        title: design.title || design.name || "",
        title_ur: design.title_ur || "",
        plot_dimension: design.plot_dimension || "",
        covered_area: design.covered_area || "",
        floors: design.floors || 0,
        customUrl: design.customUrl || "",
        description: design.description || "",
        description_ur: design.description_ur || "",
        features_ur: design.features_ur || "",
        servant_quarters: !!design.servant_quarters,
        terrace: !!design.terrace,
        car_parking: !!design.car_parking,
        images: [],
        imagePreviews: [],
        imageAlts: [],
        imageTitles: [],
        primaryImageIndex: 0,
      });
      setExistingImages(
        (design.images || [])
          .map((img, index) => ({
            ...img,
            position: img.is_primary
              ? ""
              : img.position != null && Number(img.position) >= 1
                ? img.position
                : index + 1,
          }))
          .sort((a, b) => {
            if (!!a.is_primary !== !!b.is_primary) return a.is_primary ? -1 : 1;
            return (Number(a.position) || 0) - (Number(b.position) || 0);
          }),
      );
      setImageError("");
    } catch (error) {
      console.error(
        "Failed to load corporate architecture design for edit:",
        error,
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          "Failed to load corporate architecture design for editing.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      (formData.imagePreviews || []).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [formData.imagePreviews]);

  const handleFeatureChange = (featureName) => {
    setFormData({ ...formData, [featureName]: !formData[featureName] });
  };

  const handleCancel = () => {
    setExistingImages([]);
    resetForm();
    navigate("/corporate-architecture-design");
  };

  const renderActionButtons = () => (
    <>
      <button
        type="button"
        onClick={handleCancel}
        className="ui-btn-cancel"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button type="submit" className="ui-btn-save" disabled={isSubmitting}>
        <Save size={14} />
        {isSubmitting
          ? isEditMode
            ? "Updating..."
            : "Saving..."
          : isEditMode
            ? "Update"
            : "Save"}
      </button>
    </>
  );

  return (
    <div className="house-design-form-page min-h-0">
      <form onSubmit={handleSubmit}>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="ui-btn-back"
              aria-label="Back"
              disabled={isSubmitting}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <p className="mb-0 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#982598]">
                Corporate Architecture Design
              </p>
              <h2 className="text-xl font-extrabold tracking-tight text-[#15173d]">
                {isEditMode
                  ? "Edit Corporate Architecture Design"
                  : "Add Corporate Architecture Design"}
              </h2>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#15173d]/10 bg-white px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-[#15173d]">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isEditMode ? "bg-[#982598]" : "bg-green-600"
              }`}
            />
            {isEditMode ? "Edit" : "Create"}
          </span>
        </div>

        <div className="space-y-4">
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <Home size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Basic details
                </h3>
                <p className="text-[11px] text-gray-500">
                  Title, dimensions &amp; URL
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="ui-form-label">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
                <UrduInput
                  label="عنوان"
                  name="title_ur"
                  value={formData.title_ur}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="ui-form-label">Plot Dimension</label>
                <input
                  type="text"
                  name="plot_dimension"
                  value={formData.plot_dimension}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">Covered Area</label>
                <input
                  type="text"
                  name="covered_area"
                  value={formData.covered_area}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">Floors</label>
                <input
                  type="number"
                  name="floors"
                  value={formData.floors}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">
                  Custom Url <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customUrl"
                  value={formData.customUrl}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
            </div>
          </section>

          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <FileText size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Description
                </h3>
                <p className="text-[11px] text-gray-500">
                  English &amp; Urdu content
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="ui-form-label">Description</label>
                <RichTextEditor
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Write corporate architecture design description..."
                  minHeight={180}
                  maxHeight={420}
                />
              </div>
              <div>
                <label className="ui-form-label text-right" dir="rtl">
                  تفصیل
                </label>
                <RichTextEditor
                  dir="rtl"
                  value={formData.description_ur}
                  onChange={handleDescriptionUrChange}
                  placeholder="اردو میں تفصیل لکھیں..."
                  minHeight={180}
                  maxHeight={420}
                />
              </div>
            </div>
          </section>

          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <CheckSquare size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Features
                </h3>
                <p className="text-[11px] text-gray-500">
                  Amenities in English &amp; Urdu
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="ui-form-label">Features</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    ["servant_quarters", "Servant Quarters"],
                    ["terrace", "Terrace"],
                    ["car_parking", "Car Parking"],
                  ].map(([key, label]) => (
                    <div
                      key={`en-${key}`}
                      className={featureChip}
                      onClick={() => handleFeatureChange(key)}
                    >
                      <input
                        type="checkbox"
                        id={`${key}_en`}
                        checked={formData[key]}
                        onChange={() => handleFeatureChange(key)}
                        className="h-4 w-4 accent-[#982598]"
                      />
                      <label
                        htmlFor={`${key}_en`}
                        className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                        onClick={(e) => e.preventDefault()}
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div dir="rtl">
                <h3 className="ui-form-label">خصوصیات</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    ["servant_quarters", "ملازم کا کمرہ"],
                    ["terrace", "چھت"],
                    ["car_parking", "کار پارکنگ"],
                  ].map(([key, label]) => (
                    <div
                      key={`ur-${key}`}
                      className={featureChip}
                      onClick={() => handleFeatureChange(key)}
                    >
                      <input
                        type="checkbox"
                        id={`${key}_ur`}
                        checked={formData[key]}
                        onChange={() => handleFeatureChange(key)}
                        className="h-4 w-4 accent-[#982598]"
                      />
                      <label
                        htmlFor={`${key}_ur`}
                        className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                        onClick={(e) => e.preventDefault()}
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <ImagePlus size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Gallery
                </h3>
                <p className="text-[11px] text-gray-500">
                  1200×900 images · set primary &amp; positions
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="ui-form-label">
                Upload Images <span className="text-red-500">*</span>
              </label>
              <div className="group relative rounded-2xl border-2 border-dashed border-[#982598]/35 bg-[rgba(152,37,152,0.03)] p-8 transition hover:border-[#982598] hover:bg-[rgba(152,37,152,0.06)]">
                <input
                  type="file"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="images"
                  accept="image/*"
                />
                <label
                  htmlFor="images"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#982598]/10 text-[#982598]">
                    <Upload size={22} />
                  </span>
                  <span className="text-[13px] font-extrabold text-[#15173d]">
                    Click to upload multiple images
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Only 1200×900 resolution accepted
                  </span>
                </label>
              </div>
            </div>

            {isEditMode && existingImages.length > 0 && (
              <div className="mb-4">
                <h4 className="ui-form-label">
                  Existing Images ({existingImages.length})
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {existingImages.map((image, index) => (
                    <div key={image.id || index} className="group relative">
                      <div
                        className={`relative flex h-24 items-center justify-center overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa] ${
                          image.is_primary ? "ring-2 ring-[#982598]" : ""
                        }`}
                      >
                        <img
                          src={image.image_url || image.url || image}
                          alt={`Existing ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        {image.is_primary && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-[#982598] px-1.5 py-0.5 text-[10px] font-bold text-white">
                            PRIMARY
                          </div>
                        )}
                      </div>
                      <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleSetExistingPrimary(image.id)}
                          className={`rounded-full p-1 shadow-md ${
                            image.is_primary
                              ? "bg-[#982598] text-white"
                              : "bg-white text-gray-600 hover:bg-[#982598]/10"
                          }`}
                        >
                          <Star
                            size={12}
                            fill={image.is_primary ? "currentColor" : "none"}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setExistingImages((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                          className="rounded-full bg-red-500 p-1 text-white"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {!image.is_primary ? (
                        <div className="mt-1">
                          <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                            Position
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[1-9][0-9]*"
                            value={image.position ?? ""}
                            onChange={(e) =>
                              onExistingImagePositionChange(
                                index,
                                e.target.value,
                              )
                            }
                            className="ui-input-field w-full px-1 py-1 text-xs"
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-center text-[10px] font-medium text-[#982598]">
                          Primary (not indexed)
                        </p>
                      )}
                      <ImageSeoFields
                        stacked
                        imageAlt={image.image_alt || ""}
                        imageTitle={image.image_title || ""}
                        onAltChange={(value) =>
                          onExistingImageSeoChange(index, "image_alt", value)
                        }
                        onTitleChange={(value) =>
                          onExistingImageSeoChange(index, "image_title", value)
                        }
                        className="mt-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.images.length > 0 && (
              <div className="mb-4">
                <h4 className="ui-form-label">
                  Selected Images ({formData.images.length})
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {formData.images.map((file, index) => (
                    <div key={index} className="relative">
                      <div
                        className={`relative flex h-24 items-center justify-center overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa] ${
                          index === formData.primaryImageIndex
                            ? "ring-2 ring-[#982598]"
                            : ""
                        }`}
                      >
                        {formData.imagePreviews?.[index] ? (
                          <img
                            src={formData.imagePreviews[index]}
                            alt={file.name || `Image ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                        {index === formData.primaryImageIndex && (
                          <div className="absolute left-1 top-1 rounded-full bg-[#982598] p-1 text-white shadow-md">
                            <Star size={12} fill="white" />
                          </div>
                        )}
                        {index !== formData.primaryImageIndex && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            className="absolute bottom-1 left-1 rounded-full bg-white p-1 text-gray-600 shadow-md"
                          >
                            <Star size={12} />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -right-2 -top-2 z-10 rounded-full bg-red-500 p-1 text-white shadow-md"
                      >
                        <Trash2 size={12} />
                      </button>
                      {index !== formData.primaryImageIndex ? (
                        <div className="mt-1">
                          <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                            Position
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[1-9][0-9]*"
                            value={formData.imagePositions?.[index] ?? ""}
                            onChange={(e) =>
                              onImagePositionChange(index, e.target.value)
                            }
                            className="ui-input-field w-full px-1 py-1 text-xs"
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-center text-[10px] font-medium text-[#982598]">
                          Primary (not indexed)
                        </p>
                      )}
                      <ImageSeoFields
                        stacked
                        imageAlt={formData.imageAlts?.[index] || ""}
                        imageTitle={formData.imageTitles?.[index] || ""}
                        onAltChange={(value) =>
                          onNewImageSeoChange(index, "image_alt", value)
                        }
                        onTitleChange={(value) =>
                          onNewImageSeoChange(index, "image_title", value)
                        }
                        className="mt-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {imageError ? (
              <p className="mb-2 text-sm text-red-500">{imageError}</p>
            ) : null}

            <div className="mt-6 flex justify-end gap-2 border-t border-[#15173d]/[0.07] pt-4">
              {renderActionButtons()}
            </div>
          </section>
        </div>
      </form>
    </div>
  );
};

export default ArchitectureDesignForm;
